import React, { useCallback, forwardRef, useRef } from 'react';
import { useDebounceStringProperty } from 'hooks/useDebounceStringProperty';
import classNames from 'classnames';
import './SearchField.less';
import { IconButton } from '../buttons/IconButton';
import { useTrackFocus } from './hooks/useTrackFocus';
import './text-inputs.less';
import { useTabbingContext } from 'contexts/TabbingContext';
import { Spinner } from 'components/Spinner';
import { DebounceComponent } from 'components/DebounceComponent';

export type SearchFieldVariant = 'outlined' | 'standard' | 'rounded';

interface ISearchFieldProps extends React.HTMLProps<{}> {
  onSearch: (value: string) => void;
  name?: string;
  value: string;
  onClear: () => void;
  clearTitle?: string;
  showClearIcon?: boolean;
  /**
   * Determines whether to show searchbox or not
   * @default true
   */
  showSearchIcon?: boolean;
  /**
   * submitOnEnter - even if we are allowed to just debounce this in some cases, we might need to handle
   * the enter-key / submit button for other / legacy cases.
   */
  submitOnEnter?: boolean;

  /**
   * Only execute overriden blur event handler provided by parent component
   */
  doNotEmitOnBlur?: boolean;
  /**
   * Indicates that a search process is going and loading indicator is displayed in the field
   */
  isLoading?: boolean;

  /** for legacy reasons, outlined is default. standard is material UI underline */
  variant?: SearchFieldVariant;

  /**
   * true to show only search icon when field is empty and has no focus
   *
   * Requires some expanded width setting from parent for animation.
   *
   * TODO react-spring would be better here, but don't have time just now.
   */
  showHideBehavior?: boolean;

  /**
   * true to expand to the right, otherwise to the left when `showHideBehavior` is `true`.
   */
  expandToRight?: boolean;
  /**
   * default 200, delay for onSearch callback
   */
  debounceInterval?: number;
}

const getClasses = (
  isFocused: boolean,
  className?: string,
  hasValue?: string | boolean,
  showHide?: boolean,
  variant?: SearchFieldVariant
) =>
  classNames('dpc-search-field', className, {
    'dpc-search-field--focused': isFocused,
    'dpc-search-field--hiding': showHide && !hasValue,
    'dpc-search-field--standard': variant === 'standard',
    'dpc-search-field--rounded': variant === 'rounded',
    'dpc-search-field--has-value': !!hasValue,
  });

const getInputClassNames = () => classNames('dpc-search-field__input', 'dpc-text-input');

/**
 * Search field component. Emits search string state to parent, in order to filter
 * however it sees fit.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export const SearchField = forwardRef(
  (
    {
      id,
      className,
      clearTitle,
      onClear,
      onSearch,
      value,
      onFocus,
      onBlur,
      placeholder,
      onKeyUp,
      showSearchIcon = true,
      showClearIcon = true,
      isLoading = false,
      tabIndex: propsTabIndex,
      doNotEmitOnBlur,
      submitOnEnter = false,
      showHideBehavior,
      variant,
      label,
      debounceInterval = 200,
    }: ISearchFieldProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const tabIndex = useTabbingContext(propsTabIndex);
    const inputRef = useRef<HTMLInputElement>(null);

    const { isFocused, focusTrackingProps } = useTrackFocus({
      onFocus,
      onBlur: e => {
        onBlur?.(e);
        if (!doNotEmitOnBlur && !submitOnEnter) {
          emitOnBlur();
        }
      },
    });

    const { emitOnBlur, fieldValue, setFieldValue, handleChange: debounceChange } = useDebounceStringProperty({
      value: value,
      path: [],
      onChange: onSearch,
      interval: debounceInterval,
      skipValueUpdate: isFocused,
    });

    const clear = useCallback(() => {
      inputRef.current?.focus();
      setFieldValue('');
      onClear();
    }, [onClear, setFieldValue]);

    const handleKeyUp = useCallback(
      (e: React.KeyboardEvent) => {
        if (submitOnEnter && e.key === 'Enter') {
          onSearch(fieldValue);
        }

        onKeyUp?.(e);
      },
      [fieldValue, onKeyUp, onSearch, submitOnEnter]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (submitOnEnter) {
          setFieldValue(e.target.value);
        } else {
          debounceChange(e);
        }
      },
      [debounceChange, submitOnEnter, setFieldValue]
    );

    const onSearchClick = () => {
      inputRef.current?.focus();
    };

    const onSearchMouseDown = (e: React.MouseEvent) => {
      // this will normally steal focus
      e.preventDefault();
    };

    return (
      <div id={id} className={getClasses(isFocused, className, fieldValue, showHideBehavior, variant)} ref={ref}>
        <input
          ref={inputRef}
          autoComplete="off"
          aria-label={label ?? 'Search'}
          placeholder={placeholder ?? 'Search'}
          className={getInputClassNames()}
          value={fieldValue}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
          tabIndex={tabIndex}
          {...focusTrackingProps}
        />
        {isLoading && (
          <DebounceComponent>
            <Spinner className="dpc-search-field__icon" size={26} />
          </DebounceComponent>
        )}
        {showClearIcon && fieldValue && (
          <IconButton
            className="dpc-search-field__clear"
            onClick={clear}
            title={clearTitle || 'Clear'}
            iconClassName="dpc-search-field__icon dpc-search-field__clear-icon"
            iconSize="input"
          />
        )}
        {!fieldValue && showSearchIcon && (
          <i
            onClick={onSearchClick}
            onMouseDown={onSearchMouseDown}
            className="dpc-search-field__icon dpc-search-field__search-icon"
            // TODO hover color when hiding
            // TODO should be a _button_
          />
        )}
      </div>
    );
  }
);
